import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { SFContentContainer, SFLoadingPage } from "@sf-core";
import { selectCoursesBySkillPathLevelId } from "../../redux/reducers/courseReducer";
import {
  getDomain,
  getJobCoursesWithSkills,
  selectDomainSkills,
} from "../../redux/reducers/domainReducer";
import {
  selectDomainSkillPathLevels,
  selectJobSkillPathLevels,
  getSkillPathLevels,
} from "../../redux/reducers/skillPathLevelsReducer";
import { userConfigurationActions } from "../../redux/reducers/userConfigurationReducer";
import DomainPage from "../../components/domainPage";
import { useTranslation } from "react-i18next";
import { profileActions } from "../../redux/reducers/profileReducer";
import { getProfileUrl } from "../../constants/urls";
import { PROFILE_STEPS } from "../../constants/profileSteps";
import { selectDomainsByType } from "../../redux/reducers/domainReducer";
import { DOMAIN_TYPES } from "../../constants/domainTypes";
import {
  getRelatedJobs,
  getUserDomainRecommendedSkillPaths,
} from "../../services/domainService";
import { getHomeUrl } from "../../constants/urls";
import { removeJobGoal } from "../../services/profileService";
import { removeDomainsByType } from "../../redux/reducers/domainReducer";

const JobScreen = () => {
  let { id } = useParams();
  id = id.toLowerCase();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [subTitle, setSubTitle] = useState(null);
  const userJobRoleId = useSelector(
    (state) => state.userConfiguration.jobRoleId
  );
  const userJobsOfInterest = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.USER_JOB_GOAL)
  );
  const lxpEnabledLearnerJobAndSkillVerification = useSelector(
    (state) =>
      state.session.tenantSettings.lxpEnabledLearnerJobAndSkillVerification
  );
  let isUserJobRole = userJobRoleId === id;
  let isJobOfInterest = userJobsOfInterest.some((job) => job.id === id);
  const [currentState, setCurrentState] = useState({
    skillPathLevelId: null,
    recommendedSkillPathLevelId: null,
    skillPathId: null,
    selectedSkills: [],
  });
  const [contentOptions, setContentOptions] = useState([]);
  const [recommendedSkillPaths, setRecommendedSkillPaths] = useState([]);
  const job = useSelector((state) => state.domains.find((x) => x.id === id));

  const existingJobsOfInterest = useSelector((state) => state.profile.jobGoals);

  const skillPathLevels = useSelector((state) =>
    selectDomainSkillPathLevels(state, id)
  );

  const recommendedSkillPathLevels = useSelector((state) =>
    selectJobSkillPathLevels(state).filter((level) =>
      recommendedSkillPaths.map((path) => path.levelId).includes(level.id)
    )
  );

  const courses = useSelector((state) =>
    selectCoursesBySkillPathLevelId(
      state,
      id,
      currentState.skillPathLevelId,
      currentState.selectedSkills
    )
  );

  const lastAccessed = useSelector((state) =>
    state.userConfiguration.lastAccessed.find((x) => x.domainId === id)
  );

  const allSkills = useSelector((state) => selectDomainSkills(state, id));

  useEffect(() => {
    getUserDomainRecommendedSkillPaths(id).then((response) => {
      setRecommendedSkillPaths(response.data);
    });
  }, [id]);

  useEffect(() => {
    dispatch(getDomain(id));
    dispatch(getSkillPathLevels());
    dispatch(getJobCoursesWithSkills(id));
    getRelatedJobs(id).then((response) => {
      setRelatedJobs(response.data);
    });
  }, [id]);

  useEffect(() => {
    if (skillPathLevels.length !== 0) {
      setCurrentState((prv) => ({
        ...prv,
        skillPathLevelId:
          lastAccessed?.skillPathLevelId ?? skillPathLevels[0].id,
      }));

      if (lastAccessed?.skillPathLevelId) {
        dispatch(userConfigurationActions.removeLastAccessed({ domainId: id }));
      }
      setContentOptions((prevContentOptions) =>
        prevContentOptions.some((item) => item.id === 1)
          ? prevContentOptions
          : [{ id: 1, label: t("jobScreen.tabCourses") }, ...prevContentOptions]
      );
    }
  }, [skillPathLevels.length]);

  const jobMenuTabs = useMemo(() => {
    const tabs = [
      { label: t("jobScreen.tabJobSkills") },
      ...(relatedJobs.length > 0
        ? [{ label: t("jobScreen.tabRelatedJobs") }]
        : []),
    ];
    return tabs;
  }, [relatedJobs.length]);

  // Set SubTitle for Avatar Menu
  useEffect(() => {
    if (isUserJobRole) setSubTitle(t("jobScreen.subTitle"));
    if (isJobOfInterest) setSubTitle(t("jobOfInterestScreen.subTitle"));
  }, [isUserJobRole, isJobOfInterest]);

  // Get DropDown Actions for Job Role and Job of Interest
  const getDropDownActions = (navigate, t) => {
    if (isUserJobRole && lxpEnabledLearnerJobAndSkillVerification) {
      return {
        actions: [
          {
            label: t("jobScreen.dropDownManage"),
            onClick: () => {
              dispatch(
                profileActions.setState({
                  activeStep: PROFILE_STEPS.JOB_ROLE,
                })
              );
              // Navigate to Profile Setup Job role page
              navigate(getProfileUrl());
            },
          },
        ],
      };
    }
    if (isJobOfInterest && lxpEnabledLearnerJobAndSkillVerification) {
      return {
        actions: [
          {
            label: t("jobOfInterestScreen.dropDownManage"),
            onClick: () => {
              dispatch(
                profileActions.setState({
                  activeStep: PROFILE_STEPS.JOB_INTEREST,
                })
              );
              // Navigate to Profile Setup Jobs of Interest page
              navigate(getProfileUrl());
            },
          },
          {
            label: t("jobOfInterestScreen.dropDownRemove"),
            onClick: () => {
              removeJobGoal(job?.internalReference).then(() => {
                dispatch(removeDomainsByType(DOMAIN_TYPES.USER_JOB_GOAL));
                // Remove Job of Interest also from redux state
                const jobsOfInterest = existingJobsOfInterest.filter(
                  (x) => x.id !== job?.internalReference
                );
                dispatch(profileActions.setJobGoals(jobsOfInterest));
                navigate(getHomeUrl());
              });
            },
          },
        ],
      };
    }
    return null;
  };

  if (!job) {
    return (
      <SFContentContainer>
        <SFLoadingPage />
      </SFContentContainer>
    );
  }

  return (
    <DomainPage
      backgroundImage={job.backgroundImage}
      domain={job}
      menuTabs={jobMenuTabs}
      allSkills={allSkills}
      currentState={currentState}
      setCurrentState={setCurrentState}
      skillPathLevels={skillPathLevels}
      contentOptions={contentOptions}
      setContentOptions={setContentOptions}
      recommendedSkillPathLevels={recommendedSkillPathLevels}
      courses={courses}
      recommendedSkillPaths={recommendedSkillPaths}
      relatedDomains={relatedJobs}
      showAvatar={isUserJobRole || isJobOfInterest} //True If this is user's job role or Job of Interest page
      subTitle={subTitle}
      getDropDownActions={getDropDownActions}
    />
  );
};

export default JobScreen;
